import OrderOptimizerDetailsOther, {
  OrderOptimizerDetailsOtherStepsOrdersPoints,
  OrderOptimizerDetailsOtherStepsOrders,
  OrderOptimizerDetailsOtherStepsHomeToStart,
  OrderOptimizerDetailsOtherStepsEndToHome,
  OrderOptimizerDetailsOtherSteps,
  OrderOptimizerDetailsOtherOrdersByDriverSabs,
  OrderOptimizerDetailsOtherOrdersByDriverAba,
  OrderOptimizerDetailsOtherOrders,
  OrderOptimizerDetailsOtherOptimizedStats,
  OrderOptimizerDetailsOtherUnoptimizedStats,
  OrderOptimizerDetailsOtherDriver,
  OrderOptimizerDetailsOtherStatus,
} from "../../../../../common/services/order-optimizer/details/other/order-optimizer-details-other";
import OrderOptimizerRailyDetailsItem, {
  OrderOptimizerRailyDetailsItemDriver,
  OrderOptimizerRailyDetailsItemOptimizedStats,
  OrderOptimizerRailyDetailsItemOrders,
  OrderOptimizerRailyDetailsItemOrdersByDriverAba,
  OrderOptimizerRailyDetailsItemOrdersByDriverSabs,
  OrderOptimizerRailyDetailsItemStatus,
  OrderOptimizerRailyDetailsItemSteps,
  OrderOptimizerRailyDetailsItemStepsEndToHome,
  OrderOptimizerRailyDetailsItemStepsHomeToStart,
  OrderOptimizerRailyDetailsItemStepsOrders,
  OrderOptimizerRailyDetailsItemStepsOrdersPoints,
  OrderOptimizerRailyDetailsItemUnoptimizedStats,
} from "./types/order-optimizer-raily-details-item";

const createDetailsItemStepsOrderPoint = (
  stepsOrderPoint: OrderOptimizerDetailsOtherStepsOrdersPoints,
): OrderOptimizerRailyDetailsItemStepsOrdersPoints => ({
  displayName: stepsOrderPoint.displayName,
  distance: stepsOrderPoint.distance,
  driverDistance: stepsOrderPoint.driverDistance,
  driverDrivingTime: stepsOrderPoint.driverDrivingTime,
  driverHaltingTime: stepsOrderPoint.driverHaltingTime,
  driverTime: stepsOrderPoint.driverTime,
  haltingTime: stepsOrderPoint.haltingTime,
  lat: stepsOrderPoint.lat,
  lon: stepsOrderPoint.lon,
  time: stepsOrderPoint.time,
});

const createDetailsItemStepsOrderPoints = (
  stepsOrderPoints: OrderOptimizerDetailsOtherStepsOrdersPoints[],
): OrderOptimizerRailyDetailsItemStepsOrdersPoints[] => {
  return stepsOrderPoints.map(createDetailsItemStepsOrderPoint);
};

const createDetailsItemStepsOrder = (
  stepsOrder: OrderOptimizerDetailsOtherStepsOrders,
): OrderOptimizerRailyDetailsItemStepsOrders => ({
  distance: stepsOrder.distance,
  haltingTime: stepsOrder.haltingTime,
  points: createDetailsItemStepsOrderPoints(stepsOrder.points),
  totalTime: stepsOrder.totalTime,
});

const createDetailsItemStepsOrders = (
  stepsOrders: OrderOptimizerDetailsOtherStepsOrders[],
): OrderOptimizerRailyDetailsItemStepsOrders[] => {
  return stepsOrders.map(createDetailsItemStepsOrder);
};

const createDetailsItemStepsHomeToStart = (
  stepsHomeToStart: OrderOptimizerDetailsOtherStepsHomeToStart | null,
): OrderOptimizerRailyDetailsItemStepsHomeToStart | null => {
  if (!stepsHomeToStart) return null;
  return {
    displayName: stepsHomeToStart.displayName,
    driverTime: stepsHomeToStart.driverTime,
  };
};

const createDetailsItemStepsEndToHome = (
  stepsEndToHome: OrderOptimizerDetailsOtherStepsEndToHome | null,
): OrderOptimizerRailyDetailsItemStepsEndToHome | null => {
  if (!stepsEndToHome) return null;
  return {
    displayName: stepsEndToHome.displayName,
    driverDistance: stepsEndToHome.driverDistance,
    driverDrivingTime: stepsEndToHome.driverDrivingTime,
    driverTime: stepsEndToHome.driverTime,
  };
};

const createDetailsItemSteps = (
  steps: OrderOptimizerDetailsOtherSteps,
): OrderOptimizerRailyDetailsItemSteps => {
  return {
    endToHome: createDetailsItemStepsEndToHome(steps.endToHome),
    homeToStart: createDetailsItemStepsHomeToStart(steps.homeToStart),
    orders: createDetailsItemStepsOrders(steps.orders),
  };
};

const createDetailsItemOrdersBySabs = (
  ordersBySabs: OrderOptimizerDetailsOtherOrdersByDriverSabs | null,
): OrderOptimizerRailyDetailsItemOrdersByDriverSabs | null => {
  if (!ordersBySabs) return null;
  return {
    distance: ordersBySabs.distance,
    haltingTime: ordersBySabs.haltingTime,
    totalTime: ordersBySabs.totalTime,
  };
};

const createDetailsItemOrdersByAba = (
  ordersByABa: OrderOptimizerDetailsOtherOrdersByDriverAba | null,
): OrderOptimizerRailyDetailsItemOrdersByDriverAba | null => {
  if (!ordersByABa) return null;
  return {
    distance: ordersByABa.distance,
    haltingTime: ordersByABa.haltingTime,
    totalTime: ordersByABa.totalTime,
  };
};

const createDetailsItemOrder = (
  order: OrderOptimizerDetailsOtherOrders,
): OrderOptimizerRailyDetailsItemOrders => ({
  billingModel: order.billingModel,
  cargoCompany: order.cargoCompany,
  distance: order.distance,
  driverName: order.driverName,
  fleetPartner: order.fleetPartner,
  haltingTime: order.haltingTime,
  humanId: order.humanId,
  planEntryHumanId: order.planEntryHumanId,
  startTime: order.startTime,
  taxiCorporation: order.taxiCorporation,
  totalTime: order.totalTime,
});

const createDetailsItemOrders = (
  orders: OrderOptimizerRailyDetailsItemOrders[],
): OrderOptimizerDetailsOtherOrders[] => {
  return orders.map(createDetailsItemOrder);
};

const createDetailsItemOptimizedStats = (
  optimizedStats: OrderOptimizerDetailsOtherOptimizedStats,
): OrderOptimizerRailyDetailsItemOptimizedStats => {
  return {
    contractRate: optimizedStats.contractRate,
    distance: optimizedStats.distance,
    haltingTime: optimizedStats.haltingTime,
    margin: optimizedStats.margin,
    totalTime: optimizedStats.totalTime,
  };
};

const createDetailsItemUnoptimizedStats = (
  unoptimizedStats: OrderOptimizerDetailsOtherUnoptimizedStats,
): OrderOptimizerRailyDetailsItemUnoptimizedStats => {
  return {
    contractRate: unoptimizedStats.contractRate,
    distance: unoptimizedStats.distance,
    haltingTime: unoptimizedStats.haltingTime,
    margin: unoptimizedStats.margin,
    totalTime: unoptimizedStats.totalTime,
  };
};

const createDetailsItemDriver = (
  driver: OrderOptimizerDetailsOtherDriver | null,
): OrderOptimizerRailyDetailsItemDriver | null => {
  if (!driver) return null;
  return {
    contractRate: driver.contractRate,
    displayName: driver.displayName,
    driverId: driver.driverId,
    startLat: driver.startLat,
    startLon: driver.startLon,
    taxiCorporation: driver.taxiCorporation,
  };
};

const createStatus = (
  status: OrderOptimizerDetailsOtherStatus,
): OrderOptimizerRailyDetailsItemStatus => {
  switch (status) {
    case OrderOptimizerDetailsOtherStatus.INTERESTING:
      return OrderOptimizerRailyDetailsItemStatus.INTERESTING;
    case OrderOptimizerDetailsOtherStatus.PENDING:
      return OrderOptimizerRailyDetailsItemStatus.PENDING;
    case OrderOptimizerDetailsOtherStatus.REJECTED:
      return OrderOptimizerRailyDetailsItemStatus.REJECTED;
  }
};

const createDetailsItem = (
  detailsItem: OrderOptimizerDetailsOther,
): OrderOptimizerRailyDetailsItem => {
  return {
    driver: createDetailsItemDriver(detailsItem.driver),
    optimizedStats: createDetailsItemOptimizedStats(detailsItem.optimizedStats),
    orders: createDetailsItemOrders(detailsItem.orders),
    ordersByDriverAba: createDetailsItemOrdersByAba(
      detailsItem.ordersByDriverAba,
    ),
    ordersByDriverSabs: createDetailsItemOrdersBySabs(
      detailsItem.ordersByDriverSabs,
    ),
    reviewedAt: detailsItem.reviewedAt,
    reviewedBy: detailsItem.reviewedBy,
    saAndBsDistance: detailsItem.saAndBsDistance,
    steps: createDetailsItemSteps(detailsItem.steps),
    unoptimizedStats: createDetailsItemUnoptimizedStats(
      detailsItem.unoptimizedStats,
    ),
    score: detailsItem.score,
    status: createStatus(detailsItem.status),
    totalCargoIncome: detailsItem.totalCargoIncome,
    totalDriverHaltingCost: detailsItem.totalDriverHaltingCost,
  };
};

const orderOptimizerRailyDetailsItemFactory = {
  createDetailsItem,
  createDetailsItemOrders,
};

export default orderOptimizerRailyDetailsItemFactory;
